import { useEffect, useMemo, useRef, useState } from 'react';

const MIMO_TRASU_M = 45;          // dál od linky = rozvozce uhnul
const MIMO_TRASU_BODU = 2;        // tolik GPS bodů po sobě mimo, ať jeden skok GPS trasu nepřehodí
const MIN_ODSTUP_MS = 4000;       // nejdřív tak brzo po minulém dotazu (kromě změny cíle)
const OBNOVA_MS = 90000;          // jednou za čas i bez uhnutí — provoz se mění
const OBNOVA_NAHRADNI_MS = 20000; // Mapy.com nedostupné → rovná čára, zkusit znovu dřív

/**
 * Silniční trasa od polohy řidiče k aktuální zastávce (`/api/rozvoz/route`).
 *
 * Kdy se ptá serveru:
 *   - změní se cíl,
 *   - rozvozce uhne z trasy (dva body po sobě dál než 45 m od linky),
 *   - jednou za 90 s kvůli provozu.
 * Mezi dotazy se poloha promítá na trasu: linka za řidičem se odmazává
 * a zbývající čas i vzdálenost se počítají z ujeté části — odpočítávají
 * se plynule, ne skokem po novém dotazu. Při chybě serveru zůstává
 * poslední trasa (čas nezmizí).
 *
 * Pozor na debounce: GPS chodí každou sekundu, takže časovač resetovaný
 * při každém bodu by za jízdy nikdy nedoběhl (tak to dřív bylo).
 *
 * @return {{ geometry: [number,number][], durationSec: number|null, zbyvaM: number|null, pesiOdKonceM: number, loading: boolean }}
 */
export default function useRouteGeometry({ from, to, enabled = true }) {
    const [trasa, setTrasa] = useState(null);   // { geometry, durationSec, kumul, delkaM, cil, nahradni }
    const [loading, setLoading] = useState(false);

    const posledniDotazRef = useRef(0);
    const mimoRef = useRef(0);
    const abortRef = useRef(null);
    const cilKlic = to?.lat && to?.lng ? `${to.lat},${to.lng}` : null;

    // Trasa k jinému cíli už neplatí — pryč hned, ne až po odpovědi.
    const platna = trasa && trasa.cil === cilKlic ? trasa : null;

    const promitnuti = useMemo(
        () => (platna && from?.lat && from?.lng ? promitni(platna, from.lat, from.lng) : null),
        [platna, from?.lat, from?.lng],
    );

    useEffect(() => () => abortRef.current?.abort?.(), []);

    useEffect(() => {
        if (!enabled || !from?.lat || !from?.lng || !cilKlic) return;

        const ted = Date.now();
        const zmenaCile = !platna;
        if (promitnuti && promitnuti.odchylkaM > MIMO_TRASU_M) mimoRef.current += 1;
        else mimoRef.current = 0;
        // Od náhradní rovné čáry je rozvozce „mimo" pořád — to není uhnutí.
        const uhnul = !platna?.nahradni && mimoRef.current >= MIMO_TRASU_BODU;
        const stara = ted - posledniDotazRef.current > (platna?.nahradni ? OBNOVA_NAHRADNI_MS : OBNOVA_MS);

        if (!zmenaCile && !uhnul && !stara) return;
        if (ted - posledniDotazRef.current < MIN_ODSTUP_MS) return;

        posledniDotazRef.current = ted;
        mimoRef.current = 0;
        abortRef.current?.abort?.();
        const ac = new AbortController();
        abortRef.current = ac;
        const cil = cilKlic;

        // Za jízdy i směr: server pak trasu nezačne otočkou na hlavní silnici,
        // ale povede dál k místu, kde se dá otočit (2026-10-26).
        const jede = from.speed != null && from.speed >= 3 && from.heading != null && !Number.isNaN(from.heading);
        const smer = jede ? `&smer=${Math.round(from.heading)}&rychlost=${from.speed.toFixed(1)}` : '';

        setLoading(true);
        fetch(`/api/rozvoz/route?from_lat=${from.lat}&from_lng=${from.lng}&to_lat=${to.lat}&to_lng=${to.lng}${smer}`, {
            credentials: 'same-origin', signal: ac.signal,
        })
            .then(r => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); })
            .then(json => {
                const geometry = Array.isArray(json.geometry) ? json.geometry : [];
                if (geometry.length < 2) return;
                setTrasa(pripravTrasu(geometry, json.duration_sec ?? null, cil, !!json.nahradni, json.pesi_od ?? null));
            })
            .catch(() => { /* necháme poslední trasu; další pokus po MIN_ODSTUP_MS */ })
            .finally(() => { if (abortRef.current === ac) setLoading(false); });
    }, [from?.lat, from?.lng, cilKlic, enabled]);

    if (!enabled || !platna) return { geometry: [], durationSec: null, zbyvaM: null, pesiOdKonceM: 0, loading };
    const pesiOdKonceM = platna.pesiOdKonceM;
    if (!promitnuti) return { geometry: platna.geometry, durationSec: platna.durationSec, zbyvaM: Math.round(platna.delkaM), pesiOdKonceM, loading };

    const podil = platna.delkaM > 0 ? promitnuti.zbyvaM / platna.delkaM : 1;
    return {
        geometry: promitnuti.zbytek,
        durationSec: platna.durationSec != null ? Math.round(platna.durationSec * podil) : null,
        zbyvaM: Math.round(promitnuti.zbyvaM),
        pesiOdKonceM,
        loading,
    };
}

function pripravTrasu(geometry, durationSec, cil, nahradni, pesiOd = null) {
    const kumul = [0];
    for (let i = 1; i < geometry.length; i++) {
        kumul.push(kumul[i - 1] + haversineM(geometry[i - 1][0], geometry[i - 1][1], geometry[i][0], geometry[i][1]));
    }
    const delkaM = kumul[kumul.length - 1];
    // Pěší dojezd ze serveru (index posledního bodu auto-trasy) → metry od konce.
    const pesiOdKonceM = pesiOd != null && kumul[pesiOd] != null ? Math.max(0, delkaM - kumul[pesiOd]) : 0;
    return { geometry, durationSec, kumul, delkaM, cil, nahradni, pesiOdKonceM };
}

/**
 * Promítne polohu na nejbližší úsek trasy. Vrací odchylku od linky,
 * zbývající délku a zbytek geometrie začínající v promítnutém bodě.
 */
function promitni({ geometry, kumul, delkaM }, lat, lng) {
    const kx = 111320 * Math.cos((lat * Math.PI) / 180);   // m na stupeň délky
    const ky = 110540;                                       // m na stupeň šířky
    let nej = { d2: Infinity, i: 0, t: 0 };

    for (let i = 0; i < geometry.length - 1; i++) {
        const ax = (geometry[i][1] - lng) * kx, ay = (geometry[i][0] - lat) * ky;
        const bx = (geometry[i + 1][1] - lng) * kx, by = (geometry[i + 1][0] - lat) * ky;
        const dx = bx - ax, dy = by - ay;
        const l2 = dx * dx + dy * dy;
        const t = l2 > 0 ? Math.max(0, Math.min(1, -(ax * dx + ay * dy) / l2)) : 0;
        const px = ax + t * dx, py = ay + t * dy;
        const d2 = px * px + py * py;
        if (d2 < nej.d2) nej = { d2, i, t };
    }

    const a = geometry[nej.i], b = geometry[nej.i + 1];
    const bod = [a[0] + (b[0] - a[0]) * nej.t, a[1] + (b[1] - a[1]) * nej.t];
    const ujetoM = kumul[nej.i] + (kumul[nej.i + 1] - kumul[nej.i]) * nej.t;

    return {
        odchylkaM: Math.sqrt(nej.d2),
        zbyvaM: Math.max(0, delkaM - ujetoM),
        zbytek: [bod, ...geometry.slice(nej.i + 1)],
    };
}

function haversineM(lat1, lng1, lat2, lng2) {
    const R = 6371000;
    const dLat = ((lat2 - lat1) * Math.PI) / 180;
    const dLng = ((lng2 - lng1) * Math.PI) / 180;
    const a = Math.sin(dLat / 2) ** 2
        + Math.cos((lat1 * Math.PI) / 180) * Math.cos((lat2 * Math.PI) / 180)
        * Math.sin(dLng / 2) ** 2;
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}
